import { ContestStatus, ContestType } from '@betterpool/api-types/contests-service';
import { useCallback, useMemo } from 'react';

import { FEATURE } from '~/common/enums/feature.enum';
import { useTreatments } from '~/domains/common/hooks/useTreatments';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';

import getIsContestAgainstTheSpreadEnabled from '../../team/utils/getIsContestAgainstTheSpreadEnabled';
import getIsContestConfidenceScoreEnabled from '../../team/utils/getIsContestConfidenceScoreEnabled';
import getIsContestSegmentedPayoutEnabled from '../../team/utils/getIsContestSegmentedPayoutEnabled';
import getIsContestSingleSlate from '../utils/getIsContestSingleSlate';

type ContestFeatures = {
  all: boolean;
  confidence: boolean;
  segmented_payout: boolean;
  against_the_spread: boolean;
  single_slate: boolean;
  multi_slate: boolean;
  after_start: boolean;
  after_start_multi_slate: boolean;
  is_not_pga: boolean;
};

const getContestFeatures = (contest: FullContest | Contest): ContestFeatures => ({
  all: true,
  confidence: getIsContestConfidenceScoreEnabled(contest),
  segmented_payout: getIsContestSegmentedPayoutEnabled(contest),
  against_the_spread: getIsContestAgainstTheSpreadEnabled(contest),
  single_slate: getIsContestSingleSlate(contest),
  multi_slate: !getIsContestSingleSlate(contest),
  after_start: contest.status !== ContestStatus.SCHEDULED,
  after_start_multi_slate:
    contest.status !== ContestStatus.SCHEDULED && !getIsContestSingleSlate(contest),
  is_not_pga: !contest.league.alias.startsWith('pga'),
});

const SCREENS_BY_CONTEST_TYPE = {
  [ContestType.PLAYER_TIER]: [
    'picksheet',
    'stats',
    'standings',
    'entries',
    'availability',
    'players',
  ],
  [ContestType.PLAYER_ONE_AND_DONE]: [
    'picksheet',
    'stats',
    'standings',
    'entries',
    'availability',
    'players',
  ],
  [ContestType.TEAM_SURVIVOR]: ['picksheet', 'stats', 'standings', 'entries', 'availability'],
  [ContestType.TEAM_PICK_EM]: ['picksheet', 'stats', 'standings', 'entries'],
};

export type TScreen =
  | 'picksheet'
  | 'stats'
  | 'standings'
  | 'entries'
  | 'availability'
  | 'players'
  | 'entrants';

function useIsGamecenterScreenSupportedOnNativeAppsOnly({
  contest,
  screens = [],
}: {
  contest: FullContest | Contest;
  screens?: TScreen[];
}) {
  const { isReady, treatments } = useTreatments([
    FEATURE.GAMECENTER_UNSUPPORTED_TYPES_AND_FEATURES,
  ]);
  const { config } = treatments[FEATURE.GAMECENTER_UNSUPPORTED_TYPES_AND_FEATURES] || {};
  const contestFeatures = getContestFeatures(contest);

  const safeScreens = screens.length ? screens : SCREENS_BY_CONTEST_TYPE[contest.contest_type];

  const getIsScreenSupportedOnNativeAppsOnly = useCallback(
    (screen: TScreen) => {
      if (!contest || !isReady) {
        return false;
      }

      const picksheetConfig = config?.[screen] || {};
      const configForThisContest = picksheetConfig[contest?.contest_type] || [];

      return configForThisContest?.some((type: string) => contestFeatures[type]) ?? false;
    },
    [contest, isReady, config, contestFeatures]
  );

  const isScreenSupportedOnNativeAppsOnly = useMemo(
    () => safeScreens.every(getIsScreenSupportedOnNativeAppsOnly),
    [getIsScreenSupportedOnNativeAppsOnly, safeScreens]
  );

  return {
    isReady,
    isSupported: isScreenSupportedOnNativeAppsOnly,
  };
}

export default useIsGamecenterScreenSupportedOnNativeAppsOnly;
